import api from 'configs/api';
import { RaffleTicketsFilter } from 'pages/Raffle/types/common.types';
import { TEmptyResponse, TRequestResponse } from 'types/services/common';
import { API_URLS } from 'constants/urls';
import {
  TGetCampaignTermsResponse,
  TGetDrawItemsResponse,
  TGetRaffleInBasketResponse,
  TGetRaffleResultsResponse,
  TGetUserIdentityResponse,
  TGetUserTicketsCodesResponse,
  TGetUserTicketsResponse,
  TGetWinnerItemsCountResponse,
  TGetWinnerItemsResponse,
  TRaffleInitResponse,
  TValidateUserIdentityProps,
} from 'types/raffle';
import { RaffleCart } from 'contexts/RaffleCartProvider/RaffleCartProvider';
import { setUrlParams } from 'utils/urlParams';
import { URL_PARAMS } from 'constants/urlParams';
import { Paginated, PaginationProps } from 'types/common';

export const getDrawsService = (
  sortingStrategy?: string
): TRequestResponse<TGetDrawItemsResponse> => {
  const queryString = sortingStrategy
    ? `?sortingStrategy=${sortingStrategy}`
    : '';
  return api.get(`${API_URLS.RAFFLE}/draws${queryString}`);
};

export const getUserTicketsService = ({
  filter,
  page,
  size,
}: {
  filter: RaffleTicketsFilter;
  page?: number;
  size: number;
}): TRequestResponse<TGetUserTicketsResponse> => {
  const urlParams = setUrlParams(
    {
      [URL_PARAMS.STATUS]: filter,
      ...(page && {
        [URL_PARAMS.PAGE]: page,
      }),
      [URL_PARAMS.SIZE]: size,
      ...(filter === 'WINNINGS' && {
        [URL_PARAMS.ONLY_WINNERS]: true,
      }),
    },
    false
  );
  return api.get(`${API_URLS.RAFFLE_V2}/user-draws?${urlParams}`);
};

export const getUserTicketCodesService = (
  drawId: number
): TRequestResponse<TGetUserTicketsCodesResponse> => {
  return api.get(`${API_URLS.RAFFLE}/tickets?drawId=${drawId}`);
};

export const getRafflesInBasketService = (
  drawIds: number[]
): TRequestResponse<TGetRaffleInBasketResponse> => {
  return api.get(`${API_URLS.RAFFLE}/draws?drawIds=${drawIds.join(',')}`);
};
export const getCampaignTermsService = (
  drawId: number
): TRequestResponse<TGetCampaignTermsResponse> => {
  return api.get(`${API_URLS.RAFFLE}/draws/${drawId}/description-details`);
};

export const getUserIdentityService = (): TRequestResponse<TGetUserIdentityResponse> => {
  return api.get(API_URLS.USER_IDENTITY);
};

export const validateUserIdentityService = (
  userIdentity: TValidateUserIdentityProps
): TRequestResponse<TEmptyResponse> => {
  return api.post(`${API_URLS.USER_IDENTITY}/validate`, userIdentity);
};

export const getRaffleResultsService = ({
  page,
  pageSize,
}: PaginationProps): TRequestResponse<Paginated<TGetRaffleResultsResponse>> => {
  const urlParams = setUrlParams(
    {
      [URL_PARAMS.PAGE]: page,
      [URL_PARAMS.PAGE_SIZE]: pageSize,
    },
    false
  );
  return api.get(`${API_URLS.RAFFLE_V2}/completed?${urlParams}`);
};

export const getWinnerItemsCountService = (): TRequestResponse<TGetWinnerItemsCountResponse> => {
  return api.get(`${API_URLS.RAFFLE}/tickets/winner-count`);
};

export const getWinnerItemsService = ({
  page,
  pageSize,
}: PaginationProps): TRequestResponse<TGetWinnerItemsResponse> => {
  const urlParams = setUrlParams(
    {
      [URL_PARAMS.PAGE]: page,
      [URL_PARAMS.PAGE_SIZE]: pageSize,
    },
    false
  );
  return api.get(`${API_URLS.RAFFLE}/tickets/winners?${urlParams}`);
};

export const raffleInitService = (
  cartItems: RaffleCart[]
): TRequestResponse<TRaffleInitResponse> => {
  return api.post(API_URLS.RAFFLE_INIT, {
    items: cartItems,
  });
};
